import {
  enUS as enUSPickers,
  esES as esESPickers,
  ptBR as ptBRPickers,
} from '@mui/x-date-pickers/locales';
import {
  addMinutes,
  format,
  intlFormatDistance,
  isAfter,
  isThisWeek,
  isToday,
  isValid,
  isYesterday,
  parse,
} from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { enUS, es, ptBR } from 'date-fns/locale';
import { getDefaultLocale } from '../modules/translation/TranslationContext';
import { Locales } from '../modules/translation/messages';

type DateType = Date | string;

export const localeToDateFns = {
  [Locales.ENGLISH]: enUS,
  [Locales.ENGLISH_US]: enUS,
  [Locales.PORTUGUESE]: ptBR,
  [Locales.PORTUGUESE_BR]: ptBR,
  [Locales.SPANISH]: es,
};

const getPickersLocaleText = (pickersLocale: typeof ptBRPickers) =>
  pickersLocale.components.MuiLocalizationProvider.defaultProps.localeText;

export const localeToPickersLocaleText = {
  [Locales.ENGLISH]: getPickersLocaleText(enUSPickers),
  [Locales.ENGLISH_US]: getPickersLocaleText(enUSPickers),
  [Locales.PORTUGUESE]: getPickersLocaleText(ptBRPickers),
  [Locales.PORTUGUESE_BR]: getPickersLocaleText(ptBRPickers),
  [Locales.SPANISH]: getPickersLocaleText(esESPickers),
};

export const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const IsoLocalDatetimeMask = "yyyy-MM-dd'T'HH:mm:ss";
export const DATE_STRING_FORMAT = 'yyyy-MM-dd';
export const FULL_WEEKDAY_DATE_FORMAT = 'PPPP';
export const TIME_FORMAT = 'HH:mm';
export const SHORT_DATE_TIME_FORMAT = 'd MMM, HH:mm';

export const formatDateToWithoutTimezone = (date: Date | string | null) => {
  if (date === null || !isDateValid(date)) {
    return null;
  }

  return format(date, IsoLocalDatetimeMask);
};

export const isDateOnlyYMD = (dateString?: string | Date | null) => {
  return typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};
export const formatDate = (date?: DateType | null, dateFormat?: string, timeZone?: string) => {
  const resolvedTimeZone = timeZone ?? userTimeZone;
  if (!date) {
    return '';
  }

  const parsedDate = date instanceof Date ? date : parse(date, DATE_STRING_FORMAT, new Date());
  const resolvedDate = isValid(parsedDate) ? parsedDate : new Date(date);

  if (!isValid(resolvedDate)) {
    return '';
  }

  return formatInTimeZone(resolvedDate, resolvedTimeZone, dateFormat || 'P', {
    locale: localeToDateFns[getDefaultLocale()],
  });
};

export const formatFullWeekdayDate = (date?: DateType | null, timeZone?: string) =>
  formatDate(date, FULL_WEEKDAY_DATE_FORMAT, timeZone);

export const formatTime = (date?: DateType | null, timeZone?: string) =>
  formatDate(date, TIME_FORMAT, timeZone);

export const formatShortDateTime = (date?: DateType | null, timeZone?: string) =>
  formatDate(date, SHORT_DATE_TIME_FORMAT, timeZone);

export const formatDateReturningDateObject = (date?: DateType | null) => {
  if (!date) {
    return null;
  }

  const userTimezoneOffSet = new Date().getTimezoneOffset();
  return addMinutes(new Date(date), userTimezoneOffSet);
};

export const receiveDate = (date?: DateType, dateFormat?: string) => {
  if (!date) {
    return '';
  }
  const utcDate = formatInTimeZone(new Date(date), 'UTC', dateFormat || 'P');
  const dateInClientTimezone = formatInTimeZone(
    new Date(utcDate),
    userTimeZone,
    dateFormat || 'P',
    {
      locale: localeToDateFns[getDefaultLocale()],
    },
  );

  return dateInClientTimezone;
};

export const getWeekDay = (date: Date) => {
  if (!isValid(date)) {
    return '';
  }
  const timeZonedDate = toZonedTime(date, 'UTC');

  return format(timeZonedDate, 'EEEE', { locale: localeToDateFns[getDefaultLocale()] });
};

export const getMonth = (date: Date) => {
  if (!isValid(date)) {
    return '';
  }
  const timeZonedDate = toZonedTime(date, 'UTC');

  return format(timeZonedDate, 'MMMM', { locale: localeToDateFns[getDefaultLocale()] });
};

export const getShortYear = (date: Date) => {
  if (!isValid(date)) {
    return '';
  }
  const timeZonedDate = toZonedTime(date, 'UTC');

  return format(timeZonedDate, 'yy', { locale: localeToDateFns[getDefaultLocale()] });
};

export const getYear = (date: Date) => {
  if (!isValid(date)) {
    return '';
  }
  const timeZonedDate = toZonedTime(date, 'UTC');

  return format(timeZonedDate, 'yyyy', { locale: localeToDateFns[getDefaultLocale()] });
};

export const isDateAfter = (start: Date | string, end: Date | string) =>
  isAfter(new Date(start), new Date(end));

export const isDateToday = (date: DateType) => isToday(new Date(date));

export const isDateYesterday = (date: DateType) => isYesterday(new Date(date));

export const isDateThisWeek = (date: DateType) => isThisWeek(new Date(date));

export const isDateValid = (date: DateType) => isValid(new Date(date));

export const isOptionalDateValid = (date: string | null | undefined) =>
  date === null || date === undefined || isDateValid(date);

export const parseLocaleDate = (value: string) =>
  parse(value, 'P', new Date(), { locale: localeToDateFns[getDefaultLocale()] });

export const formatDateToApi = (dateString?: string | Date | null): string | null => {
  if (!dateString) {
    return null;
  }

  if (isDateOnlyYMD(dateString)) {
    return typeof dateString === 'string' ? dateString : null;
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return getCalendarDate(date);
};

export const formatDateFromApi = (date: any, pattern: string = DATE_STRING_FORMAT) => {
  if (!date) {
    return null;
  }
  return isDateValid(date) ? formatInTimeZone(date, 'UTC', pattern) : '';
};

export const validateDate = (value: any) => {
  if (!value) {
    return undefined;
  }
  return isValid(new Date(value)) ? undefined : '';
};

export const getBestTimeFormat = (
  date1: Date,
  date2: Date,
  locale: Locales = Locales.PORTUGUESE_BR,
) =>
  intlFormatDistance(date1, date2, {
    locale,
  });

export const toDateOrNull = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const normalizedDate = new Date(value);
    return isNaN(normalizedDate.getTime()) ? null : normalizedDate;
  }

  return null;
};

export const plainDateTime = (value: unknown): Date | null => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const parsed = parse(value, IsoLocalDatetimeMask, new Date());

  return isValid(parsed) ? parsed : null;
};

export const hasDateChanged = (newDate: Date | null, originalRaw: unknown): boolean => {
  const original = toDateOrNull(originalRaw);
  return newDate?.getTime() !== original?.getTime();
};

export function sortByDateDesc<T>(items: T[], dateKey: keyof T): T[] {
  return [...items].sort((a, b) => {
    const timeA = new Date(a[dateKey] as string).getTime();
    const timeB = new Date(b[dateKey] as string).getTime();

    const isInvalidA = Number.isNaN(timeA);
    const isInvalidB = Number.isNaN(timeB);

    if (isInvalidA && !isInvalidB) {
      return 1;
    }

    if (!isInvalidA && isInvalidB) {
      return -1;
    }

    if (isInvalidA && isInvalidB) {
      return 0;
    }

    return timeB - timeA;
  });
}

export function getCalendarDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getLocalizedDateWithSeparator(date: Date, separator?: string) {
  const localizedDate = formatDate(date);

  if (!localizedDate) {
    return '';
  }

  if (separator === undefined) {
    return localizedDate;
  }

  return localizedDate.replace(/[./-]/g, separator);
}

export const getDateStringValue = (value: string | null) => {
  if (!value) {
    return null;
  }

  return parse(value, DATE_STRING_FORMAT, new Date());
};

export const formatDateString = (value: DateType | null, token?: 'P' | 'P p') => {
  if (!value) {
    return null;
  }

  return format(value, token ?? DATE_STRING_FORMAT, {
    locale: localeToDateFns[getDefaultLocale()],
  });
};

export const validateDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
) => !startDate || !endDate || new Date(startDate) <= new Date(endDate);

export const toUtcIso = (localDateTime: string) => {
  if (!localDateTime) {
    return '';
  }

  const date = new Date(localDateTime);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString();
};

export const toLocalDateTimeInput = (iso: string) => {
  if (!iso) {
    return '';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value: number) => `${value}`.padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};
